import { CheckCircleOutlined } from '@ant-design/icons';
import { Descriptions, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';

import { FacilityDto, FacilityInterestedNurseDto } from '@clh/api-client';
import { TravelWillingnessLabels, WorkTypePreferenceLabels } from '@clh/util';

import { useApiClient } from '../../hooks/use-api-client';
import { useSelection } from '../../hooks/use-selection';

const { WEBAPP_BASE_URL } = ENV_CONFIG_VARS;

export default function InterestsTab() {
    const api = useApiClient();
    const { selection } = useSelection<FacilityDto>();
    const [interests, setInterests] = useState<FacilityInterestedNurseDto[]>(
        []
    );
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!api) {
            return;
        }

        setIsLoading(true);
        api.facilityControllerListInterestedNurses({
            facilityId: selection.id,
        })
            .then(setInterests)
            .finally(() => setIsLoading(false));
    }, [selection.id]);

    return (
        <div>
            <Typography.Title level={4}>Interested Nurses</Typography.Title>
            <Table
                rowKey="nurseProfileId"
                bordered
                size="small"
                dataSource={interests}
                loading={isLoading}
                expandable={{
                    expandedRowRender: (row) => (
                        <Descriptions size="small" column={1} bordered>
                            <Descriptions.Item label="Specialties">
                                {row.interestSpecialtyCodes?.map((s) => (
                                    <Tag key={s}>{s}</Tag>
                                ))}
                            </Descriptions.Item>
                            <Descriptions.Item label="Start Date">
                                {row.startDateComment}
                            </Descriptions.Item>
                            <Descriptions.Item label="Comment">
                                {row.comment}
                            </Descriptions.Item>
                        </Descriptions>
                    ),
                }}
                columns={[
                    {
                        title: 'Nurse',
                        render: (_, row) => (
                            <a
                                href={`${WEBAPP_BASE_URL}/site/nurse/${row.nurseProfileId}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {row.firstName} {row.lastName}
                            </a>
                        ),
                        sorter: (a, b) =>
                            `${a.lastName}${a.firstName}`.localeCompare(
                                `${b.lastName}${b.firstName}`
                            ),
                    },
                    {
                        title: 'Work Pref',
                        dataIndex: 'workTypePreference',
                        render: (val: string) =>
                            WorkTypePreferenceLabels[val] ?? val,
                        sorter: (a, b) =>
                            (a.workTypePreference ?? '').localeCompare(
                                b.workTypePreference ?? ''
                            ),
                    },
                    {
                        title: 'Travel',
                        dataIndex: 'willingToTravel',
                        render: (val: string) =>
                            TravelWillingnessLabels[val] ?? val,
                        sorter: (a, b) =>
                            (a.willingToTravel ?? '').localeCompare(
                                b.willingToTravel ?? ''
                            ),
                    },
                    {
                        title: 'Submitted',
                        dataIndex: 'createdAt',
                        render: (value: Date) =>
                            value ? new Date(value).toLocaleDateString() : '',
                        sorter: (a, b) =>
                            new Date(a.createdAt).getTime() -
                            new Date(b.createdAt).getTime(),
                        defaultSortOrder: 'descend',
                    },
                    {
                        title: 'Complete',
                        width: 80,
                        render: (_, row) =>
                            row.isProfileComplete ? (
                                <CheckCircleOutlined />
                            ) : null,
                    },
                ]}
            />
        </div>
    );
}
